import { crsLeadSubmitted } from "@/lib/analytics-events";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { CheckCircle2, AlertCircle, Loader2, Upload, type LucideIcon } from "lucide-react";
import { submitEnquiry } from "@/lib/forms.functions";
import { submitGrantsAssessment } from "@/lib/grants-assessment.functions";
import { submissionSchema, type SubmissionInput } from "@/lib/form-schema";
import { navigateToCompleteYourEstimate } from "@/lib/estimate-carry-forward";
import {
  GRANTS_ATTACHMENT_ACCEPT,
  GRANTS_ATTACHMENT_MAX_BYTES,
  validateGrantsAttachmentFile,
} from "@/lib/grants-assessment-schema";
import { SERVICES } from "@/lib/services";
import {
  REMEDIAL_WORKS,
  RETROFIT_ENQUIRY_OPTION_LABEL,
  RETROFIT_ENQUIRY_OPTION_VALUE,
  RETROFIT_MEASURE_TILES,
  type IconTile,
} from "@/content/enquiry-retrofit";
import { cn } from "@/lib/utils";
import { SYMPTOM_FUNNEL_EVENT, type SymptomFunnelDetail } from "@/context/symptom-context";
import { useFinancePostcode } from "@/context/finance-postcode-context";

type FormType = SubmissionInput["form_type"];

const VISIBLE_FIELD_ERRORS = new Set([
  "name",
  "phone",
  "email",
  "postcode",
  "service_of_interest",
  "message",
  "attachment",
]);

function scrollToFirstFormIssue(form: HTMLFormElement) {
  requestAnimationFrame(() => {
    const target =
      form.querySelector<HTMLElement>('[aria-invalid="true"]') ??
      form.querySelector<HTMLElement>('[role="alert"]') ??
      form.querySelector<HTMLElement>(".text-destructive, [class*='bg-destructive']");
    target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  });
}

interface EnquiryFormProps {
  formType: FormType;
  variant?: "survey" | "quote" | "contact" | "lp-quote";
  defaultService?: string;
  defaultPostcode?: string;
  sourcePage?: string;
  title?: string;
  subtitle?: string;
  className?: string;
  submitLabel?: string;
  successLabel?: string;
  phoneLabel?: string;
  phoneTel?: string;
  /** After a successful enquiry webhook, navigate to the post-enquiry details page. */
  completeEstimateOnSuccess?: boolean;
}

const VARIANT_FIELDS: Record<
  NonNullable<EnquiryFormProps["variant"]>,
  { name: boolean; phone: boolean; email: boolean; postcode: boolean; service: boolean; message: boolean }
> = {
  survey: { name: true, phone: true, email: true, postcode: true, service: true, message: true },
  quote: { name: true, phone: true, email: true, postcode: true, service: false, message: false },
  contact: { name: true, phone: true, email: true, postcode: false, service: false, message: true },
  "lp-quote": { name: true, phone: true, email: true, postcode: true, service: false, message: true },
};

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Could not read file"));
        return;
      }
      const base64 = result.split(",")[1];
      if (!base64) {
        reject(new Error("Could not read file"));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error ?? new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

export function EnquiryForm({
  formType,
  variant = "survey",
  defaultService,
  defaultPostcode,
  sourcePage,
  title,
  subtitle,
  className,
  submitLabel,
  successLabel = "Thanks: we'll be in touch shortly.",
  phoneLabel = "0800 009 2992",
  phoneTel = "tel:+448000092992",
  completeEstimateOnSuccess = false,
}: EnquiryFormProps) {
  const submit = useServerFn(submitEnquiry);
  const submitRetrofit = useServerFn(submitGrantsAssessment);
  const navigate = useNavigate();
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [selectedService, setSelectedService] = useState<string>(defaultService ?? "");
  const [remedialWorks, setRemedialWorks] = useState<Set<string>>(new Set());
  const [measures, setMeasures] = useState<Set<string>>(new Set());
  const [assessmentFile, setAssessmentFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { postcode: financePostcode } = useFinancePostcode();
  const [postcode, setPostcode] = useState(defaultPostcode ?? financePostcode ?? "");

  useEffect(() => {
    setPostcode(defaultPostcode ?? financePostcode ?? "");
  }, [defaultPostcode, financePostcode]);

  const fields = VARIANT_FIELDS[variant];
  const isRetrofit = fields.service && selectedService === RETROFIT_ENQUIRY_OPTION_VALUE;

  // Capture GCLID from URL and persist for 90 days (Google Ads attribution window)
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      const gclid = params.get("gclid");
      if (gclid) {
        const payload = JSON.stringify({ value: gclid, expiresAt: Date.now() + 90 * 24 * 60 * 60 * 1000 });
        window.localStorage.setItem("crs_gclid", payload);
      }
    } catch { /* ignore storage errors */ }
  }, []);

  useEffect(() => {
    if (!fields.message) return;
    function onFunnel(e: Event) {
      const detail = (e as CustomEvent<SymptomFunnelDetail>).detail;
      if (detail && typeof detail.message === "string") {
        setMessage(detail.message);
      }
    }
    window.addEventListener(SYMPTOM_FUNNEL_EVENT, onFunnel);
    return () => window.removeEventListener(SYMPTOM_FUNNEL_EVENT, onFunnel);
  }, [fields.message]);

  function getStoredGclid(): string | undefined {
    if (typeof window === "undefined") return undefined;
    try {
      const fromUrl = new URLSearchParams(window.location.search).get("gclid");
      if (fromUrl) return fromUrl;
      const raw = window.localStorage.getItem("crs_gclid");
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as { value?: string; expiresAt?: number };
      if (parsed.value && parsed.expiresAt && parsed.expiresAt > Date.now()) {
        return parsed.value;
      }
    } catch { /* ignore */ }
    return undefined;
  }

  function toggleFromSet(setter: (fn: (prev: Set<string>) => Set<string>) => void, key: string) {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function onAssessmentFileChange(file: File | null) {
    setErrors((prev) => {
      const next = { ...prev };
      delete next.attachment;
      return next;
    });
    if (!file) {
      setAssessmentFile(null);
      return;
    }
    const validationError = validateGrantsAttachmentFile(file);
    if (validationError) {
      setAssessmentFile(null);
      setErrors((prev) => ({ ...prev, attachment: validationError }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setAssessmentFile(file);
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setErrors({});
    setFormError(null);

    const fd = new FormData(e.currentTarget);
    const name = ((fd.get("name") as string) || "").trim();
    const phone = ((fd.get("phone") as string) || "").trim();
    const email = ((fd.get("email") as string) || "").trim();
    const postcode = ((fd.get("postcode") as string) || "").trim();
    const userMessage = ((fd.get("message") as string) || "").trim();
    // Password managers sometimes autofill off-screen honeypot fields. If the
    // visitor filled real contact details, treat a populated honeypot as autofill
    // rather than blocking submit with an invisible validation error.
    let honeypot = (fd.get("company_website") as string) || "";
    if (honeypot && name && phone) honeypot = "";
    const source =
      sourcePage || (typeof window !== "undefined" ? window.location.pathname : "unknown");

    const formEl = e.currentTarget;

    // Retrofit assessment quotation route
    if (isRetrofit) {
      const fieldErrors: Record<string, string> = {};
      if (!name) fieldErrors.name = "Name is required";
      if (!phone) fieldErrors.phone = "Phone is required";
      if (!postcode) fieldErrors.postcode = "Postcode is required";
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        fieldErrors.email = "Please enter a valid email address";
      }
      if (Object.keys(fieldErrors).length > 0) {
        setErrors(fieldErrors);
        setStatus("idle");
        scrollToFirstFormIssue(formEl);
        return;
      }

      const remedialLabels = REMEDIAL_WORKS.filter((r) => remedialWorks.has(r.key)).map(
        (r) => r.label
      );
      const measureLabels = RETROFIT_MEASURE_TILES.filter((m) => measures.has(m.key)).map(
        (m) => m.label
      );

      const composed = [
        "Route: Retrofit Assessment Quotation",
        `Remedial works: ${remedialLabels.length ? remedialLabels.join(", ") : "None reported"}`,
        `Recommended measures: ${measureLabels.length ? measureLabels.join(", ") : "Not specified"}`,
        userMessage ? `\nNote: ${userMessage}` : null,
      ]
        .filter(Boolean)
        .join("\n");

      try {
        let attachment:
          | {
              fileName: string;
              contentType:
                | "application/pdf"
                | "application/msword"
                | "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
              base64: string;
            }
          | undefined;
        if (assessmentFile) {
          attachment = {
            fileName: assessmentFile.name,
            contentType: assessmentFile.type as
              | "application/pdf"
              | "application/msword"
              | "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            base64: await readFileAsBase64(assessmentFile),
          };
        }

        const result = await submitRetrofit({
          data: {
            name,
            phone,
            email: email || "",
            postcode,
            property_type: "Retrofit Assessment Quotation",
            message: composed,
            consent: true,
            source_page: source,
            gclid: getStoredGclid(),
            honeypot,
            attachment,
          },
        });

        if (result.success) {
          if (result.id) crsLeadSubmitted({ leadId: result.id, formId: "enquiry_grants", leadType: "grants" });
          try { formEl.reset(); } catch { /* form may have unmounted */ }
          setMessage("");
          setRemedialWorks(new Set());
          setMeasures(new Set());
          setAssessmentFile(null);
          setSelectedService("");
          setStatus("success");
          toast.success("Enquiry sent", {
            description: "Thanks - we'll review your assessment and be in touch shortly.",
          });
          try {
            await Promise.resolve(navigate({ to: "/thank-you" }));
          } catch (navErr) {
            console.error(navErr);
            setFormError("Enquiry sent, but we couldn't open the next page. Please call us if you need anything else.");
            setStatus("error");
            scrollToFirstFormIssue(formEl);
          }
        } else {
          setFormError(result.error ?? "Something went wrong. Please try again.");
          setStatus("error");
          scrollToFirstFormIssue(formEl);
        }
      } catch (err) {
        console.error(err);
        setFormError(`Network error. Please call us on ${phoneLabel}.`);
        setStatus("error");
        scrollToFirstFormIssue(formEl);
      }
      return;
    }

    // Standard enquiry
    const raw = {
      form_type: formType,
      name: name || undefined,
      phone: phone || undefined,
      email: email || undefined,
      postcode: postcode || undefined,
      service_of_interest: selectedService || defaultService || undefined,
      message: userMessage || undefined,
      source_page: source,
      gclid: getStoredGclid(),
      honeypot,
    };

    const parsed = submissionSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? "form");
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      setStatus("idle");
      const hasHiddenError = Object.keys(fieldErrors).some((key) => !VISIBLE_FIELD_ERRORS.has(key));
      if (hasHiddenError) {
        setFormError("We couldn't send your enquiry. Please try again or call us.");
      }
      scrollToFirstFormIssue(formEl);
      return;
    }

    try {
      const result = await submit({ data: parsed.data });
      if (result.success) {
        if (result.id) crsLeadSubmitted({ leadId: result.id, formId: "enquiry", leadType: "other" });
        try { formEl.reset(); } catch { /* form may have unmounted */ }
        setMessage("");
        setStatus("success");
        toast.success("Enquiry sent", {
          description: "Thanks - we'll be in touch shortly.",
        });
        try {
          if (completeEstimateOnSuccess) {
            await Promise.resolve(
              navigateToCompleteYourEstimate(navigate, {
                name,
                email,
                phone,
                postcode,
                sourcePage: source,
              }),
            );
          } else {
            await Promise.resolve(navigate({ to: "/thank-you" }));
          }
        } catch (navErr) {
          console.error(navErr);
          setFormError("Enquiry sent, but we couldn't open the next page. Please call us if you need anything else.");
          setStatus("error");
          scrollToFirstFormIssue(formEl);
        }
      } else {
        setFormError(result.error ?? "Something went wrong. Please try again.");
        setStatus("error");
        scrollToFirstFormIssue(formEl);
      }
    } catch (err) {
      console.error(err);
      setFormError(`Network error. Please call us on ${phoneLabel}.`);
      setStatus("error");
      scrollToFirstFormIssue(formEl);
    }
  }

  if (status === "success") {
    return (
      <div className={cn("rounded-2xl bg-card p-8 shadow-lift border", className)}>
        <div className="flex flex-col items-center text-center gap-3 py-6">
          <div className="rounded-full bg-success/10 p-3">
            <CheckCircle2 className="h-10 w-10 text-success" aria-hidden />
          </div>
          <h3 className="text-xl font-semibold">{successLabel}</h3>
          <p className="text-sm text-muted-foreground">
            We've received your enquiry and one of the team will be in touch shortly. For urgent matters please call us on{" "}
            <a href={phoneTel} className="text-accent font-medium">
              {phoneLabel}
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      method="post"
      onSubmit={handleSubmit}
      className={cn("rounded-2xl bg-card p-4 sm:p-6 md:p-7 shadow-lift border", className)}
      noValidate
    >
      {title && <h3 className="text-xl font-semibold mb-1 text-balance">{title}</h3>}
      {subtitle && <p className="text-sm text-muted-foreground mb-4">{subtitle}</p>}

      <div className="space-y-3">
        {/* Honeypot */}
        <div aria-hidden className="absolute left-[-9999px] w-px h-px overflow-hidden">
          <label>
            Company website
            <input type="text" name="company_website" tabIndex={-1} autoComplete="off" />
          </label>
        </div>

        {fields.name && (
          <Field label="Name" name="name" required type="text" error={errors.name} autoComplete="name" />
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          {fields.phone && (
            <Field label="Phone" name="phone" required type="tel" error={errors.phone} autoComplete="tel" />
          )}
          {fields.email && (
            <Field label="Email (optional)" name="email" type="email" error={errors.email} autoComplete="email" />
          )}
        </div>
        {fields.postcode && (
          <Field
            label="Postcode"
            name="postcode"
            required
            type="text"
            error={errors.postcode}
            autoComplete="postal-code"
            maxLength={20}
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
          />
        )}
        {fields.service && (
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Service of interest
            </label>
            <select
              name="service_of_interest"
              value={selectedService}
              onChange={(e) => setSelectedService(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3.5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
              aria-label="Service of interest"
            >
              <option value="">Not sure: please advise</option>
              {SERVICES.map((s) => (
                <option key={s.slug} value={s.shortName}>
                  {s.shortName}
                </option>
              ))}
              <option value={RETROFIT_ENQUIRY_OPTION_VALUE}>
                {RETROFIT_ENQUIRY_OPTION_LABEL}
              </option>

            </select>
          </div>
        )}

        {isRetrofit && (
          <div className="space-y-5 pt-2">
            <IconToggleGrid
              label="Was any work required before your scheme installation can go ahead?"
              helpText="If your retrofit assessment says any remedial or preparation work is needed before installation, please select the relevant items below."
              options={REMEDIAL_WORKS}
              selected={remedialWorks}
              onToggle={(key) => toggleFromSet(setRemedialWorks, key)}
            />
            <IconToggleGrid
              label="Which scheme measures were you recommended for?"
              helpText="Please select the measures included in your retrofit assessment."

              options={RETROFIT_MEASURE_TILES}
              selected={measures}
              onToggle={(key) => toggleFromSet(setMeasures, key)}
            />
            <div>
              <label htmlFor="retrofit-assessment-upload" className="block text-sm font-semibold mb-1">
                Upload Retrofit Assessment PDF
              </label>
              <p className="text-xs text-muted-foreground mb-2 leading-relaxed">
                If you have already had a retrofit assessment completed, please upload the PDF report
                here. This allows our team to review the recommendations and provide a more accurate
                quotation before arranging a survey.
              </p>
              <div
                className={cn(
                  "rounded-xl border border-dashed border-border bg-secondary/30 p-4",
                  errors.attachment && "border-destructive"
                )}
              >
                <input
                  ref={fileInputRef}
                  id="retrofit-assessment-upload"
                  name="attachment"
                  type="file"
                  accept={GRANTS_ATTACHMENT_ACCEPT}
                  className="sr-only"
                  onChange={(ev) => onAssessmentFileChange(ev.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-full border border-input bg-background px-4 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors"
                >
                  <Upload className="h-4 w-4" aria-hidden />
                  {assessmentFile ? "Change file" : "Choose file"}
                </button>
                {assessmentFile && (
                  <p className="mt-2 text-sm text-foreground">
                    {assessmentFile.name}{" "}
                    <span className="text-muted-foreground">
                      ({(assessmentFile.size / (1024 * 1024)).toFixed(1)} MB)
                    </span>
                  </p>
                )}
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                  PDF or Word, max {GRANTS_ATTACHMENT_MAX_BYTES / (1024 * 1024)}MB.
                </p>
              </div>
              {errors.attachment && (
                <p className="mt-1 text-xs text-destructive">{errors.attachment}</p>
              )}
            </div>
          </div>
        )}

        {fields.message && !isRetrofit && (
          <div>
            <label className="block text-sm font-medium mb-1.5">
              {variant === "contact" ? "Message" : "Anything we should know? (optional)"}
            </label>
            <textarea
              name="message"
              rows={variant === "contact" ? 5 : 3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3.5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              maxLength={5000}
              aria-label="Message"
            />
          </div>
        )}

        {isRetrofit && (
          <div>
            <label className="block text-sm font-medium mb-1.5">
              Anything else we should know? (optional)
            </label>
            <textarea
              name="message"
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-lg border border-input bg-background px-3.5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              maxLength={5000}
              aria-label="Message"
            />
          </div>
        )}
      </div>

      {formError && (
        <div
          className="mt-4 flex items-start gap-2 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
          <span>{formError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 text-base font-semibold hover:bg-accent/90 transition-colors shadow-cta disabled:opacity-70"
      >
        {status === "submitting" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
        {submitLabel ??
          (isRetrofit
            ? "Send For Quotation"
            : variant === "contact"
              ? "Send message"
              : "Request a callback")}
      </button>
      <p className="mt-3 text-xs text-muted-foreground text-center">
        By submitting you agree to our privacy policy. We never share your details.
      </p>
    </form>
  );
}

function IconToggleGrid({
  label,
  helpText,
  options,
  selected,
  onToggle,
}: {
  label: string;
  helpText: string;
  options: IconTile[];
  selected: Set<string>;
  onToggle: (key: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-semibold mb-1">{label}</label>
      <p className="text-xs text-muted-foreground mb-2 leading-relaxed">{helpText}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {options.map((opt) => {
          const isSelected = selected.has(opt.key);
          const Icon: LucideIcon = opt.icon;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onToggle(opt.key)}
              aria-pressed={isSelected}
              className={cn(
                "flex flex-col items-center justify-center gap-1.5 rounded-xl border bg-card px-2 py-3 text-center text-xs font-medium text-foreground transition-colors",
                "hover:bg-secondary/60",
                isSelected
                  ? "border-accent bg-accent/10 ring-2 ring-accent"
                  : "border-border"
              )}
            >
              <Icon className="h-5 w-5" aria-hidden />
              <span className="leading-tight">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type,
  required,
  error,
  autoComplete,
  maxLength,
  defaultValue,
  value,
  onChange,
}: {
  label: string;
  name: string;
  type: string;
  required?: boolean;
  error?: string;
  autoComplete?: string;
  maxLength?: number;
  defaultValue?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const isControlled = value !== undefined;
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">
        {label}
        {required && <span className="text-accent ml-0.5">*</span>}
      </label>
      <input
        type={type}
        name={name}
        required={required}
        autoComplete={autoComplete}
        maxLength={maxLength}
        defaultValue={isControlled ? undefined : defaultValue}
        value={isControlled ? value : undefined}
        onChange={isControlled ? onChange : undefined}
        aria-invalid={!!error}
        aria-label={label}
        className={cn(
          "w-full rounded-lg border border-input bg-background px-3.5 py-3 text-base focus:outline-none focus:ring-2 focus:ring-ring",
          error && "border-destructive focus:ring-destructive"
        )}
      />
      {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
    </div>
  );
}
